/**
 * The design document format.
 *
 * A design document is a FILE in the Unreal project's folder — Markdown with
 * YAML frontmatter — and this module is the only thing that reads or writes it.
 * Same decision as everything else about a project in the dev app: a project is
 * a directory, there is no source control in GOOD BUILD, and nothing about a
 * design should need our app installed to be readable.
 *
 * Which makes the round trip the load-bearing property here. These files are
 * opened in other editors, committed to whatever version control the Unreal
 * project is under, and diffed by people. A parse/serialise cycle that reflows
 * whitespace or reorders frontmatter would turn every save into a diff nobody
 * can review — so `serialise(parse(src)) === src` for any document this app did
 * not change, and that is what `design.test.ts` spends most of its length on.
 *
 * Two consequences of that promise are worth reading before changing anything:
 *
 *   • Unknown frontmatter keys are preserved VERBATIM (`meta.extra`). Somebody
 *     will put a Jira id or a Perforce path up there, and losing it on the next
 *     save is worse than not understanding it.
 *   • A tier marker is only written back when it was written by hand or set in
 *     the UI (`tierExplicit`). A document typed in another editor with no
 *     markers at all opens fine, reads as tier 2, and saves without gaining a
 *     marker on every heading.
 *
 * See `docs/spec/09-design-docs.md` for why tiers exist at all.
 */

import { z } from 'zod';

/* ----------------------------------------------------------------- tiers */

/**
 * How much a section matters, which is the whole input to the compression that
 * produces the one-pager.
 *
 * Deliberately the AUTHOR'S judgement rather than the model's. A model asked to
 * decide what matters in a design document decides differently on every run,
 * which makes two sheets generated from an unchanged document disagree — and
 * the one thing a printed sheet has to be is stable.
 */
export const DESIGN_TIERS = [1, 2, 3] as const;
export type DesignTier = (typeof DESIGN_TIERS)[number];

export function isDesignTier(value: unknown): value is DesignTier {
  return value === 1 || value === 2 || value === 3;
}

/**
 * What an unmarked section is worth.
 *
 * Two, not one: a document written before this app existed has no markers, and
 * defaulting it to tier 1 would put every heading of it on the sheet and trip
 * the overflow finding on the first generate. Two means "include it if it
 * fits", which is the honest reading of a section whose author never said.
 */
export const DEFAULT_DESIGN_TIER: DesignTier = 2;

export const DESIGN_TIER_LABELS: Record<DesignTier, string> = {
  1: 'Essential',
  2: 'Supporting',
  3: 'Background',
};

/** Shown next to the tier control, and given to the model so it compresses the way the author meant. */
export const DESIGN_TIER_MEANINGS: Record<DesignTier, string> = {
  1: 'Somebody who reads only this understands what we are building. Always on the one-pager.',
  2: 'Detail a person doing the work needs. On the one-pager if it fits.',
  3: 'Context, history, rejected options, reasoning. Never on the one-pager.',
};

/* ------------------------------------------------------------- audiences */

/**
 * Who a given sheet is drawn for.
 *
 * The architect's drawing from the talk: the client wants the elevation, the
 * contractor wants the spacing between the studs, and neither one is a summary
 * of the other. Audience changes emphasis and inclusion. It never changes a
 * fact, and nothing here may produce two sheets that contradict each other.
 */
export const DESIGN_AUDIENCES = ['team', 'exec', 'engineering', 'art', 'marketing'] as const;
export type DesignAudience = (typeof DESIGN_AUDIENCES)[number];

export function isDesignAudience(value: unknown): value is DesignAudience {
  return typeof value === 'string' && (DESIGN_AUDIENCES as readonly string[]).includes(value);
}

export const DESIGN_AUDIENCE_LABELS: Record<DesignAudience, string> = {
  team: 'The team',
  exec: 'Executives',
  engineering: 'Engineering',
  art: 'Art',
  marketing: 'Marketing',
};

/** Keeps and drops, in the words handed to the model. */
export const DESIGN_AUDIENCE_BRIEFS: Record<DesignAudience, { keeps: string; drops: string }> = {
  team: {
    keeps: 'everything essential, and supporting detail as it fits',
    drops: 'nothing in particular',
  },
  exec: {
    keeps: 'the claim, the shape of the design, time, scale and risk',
    drops: 'mechanism detail and implementation',
  },
  engineering: {
    keeps: 'systems, flows, matrices, numbers and edge cases',
    drops: 'pitch framing and language about feel',
  },
  art: {
    keeps: 'feel, presentation, the map or space, scale and counts',
    drops: 'formulas and tuning numbers',
  },
  marketing: {
    keeps: 'the claim, the moments a player will talk about, the hook',
    drops: 'systems and internals',
  },
};

/* ----------------------------------------------------------------- paper */

/**
 * One paper size the sheet can be drawn at.
 *
 * `electron` is what `webContents.printToPDF` is given. Every size here is one
 * of the names it already knows, so nothing in the print path has to convert
 * millimetres to inches and be wrong by a rounding error on the plotter.
 */
export interface PaperSpec {
  id: PaperId;
  label: string;
  widthMm: number;
  heightMm: number;
  /**
   * Which stack of sizes this belongs to.
   *
   * A studio prints on one of them. Offering somebody on Letter the next ISO
   * size up is an answer their printer does not have a tray for.
   */
  family: 'iso' | 'us';
  /**
   * The `pageSize` name passed to `printToPDF`.
   *
   * A closed union rather than `string` so a typo here is a compile error in the
   * print path. Every member is a size Electron already knows, which is what
   * keeps millimetre-to-inch arithmetic out of the one place a rounding error
   * would show up as a wrong-sized plot.
   */
  electron: 'Letter' | 'Legal' | 'Tabloid' | 'A0' | 'A1' | 'A2' | 'A3' | 'A4';
}

export const PAPER_IDS = ['Letter', 'A4', 'Legal', 'Tabloid', 'A3', 'A2', 'A1', 'A0'] as const;
export type PaperId = (typeof PAPER_IDS)[number];

/**
 * Ordered smallest to largest BY AREA, which the test pins.
 *
 * Not by family and not alphabetically: the overflow finding offers the next
 * size up, and that is only honest advice if the next entry is actually bigger.
 * US Letter is 2,000 mm² SMALLER than A4 despite being the wider sheet, and
 * Tabloid is smaller than A3 — so a list that reads A4, Letter, …, A3, Tabloid
 * hands somebody a sheet with less room than the one that just overflowed.
 */
export const PAPER_SIZES: readonly PaperSpec[] = [
  {
    id: 'Letter',
    label: 'US Letter',
    widthMm: 215.9,
    heightMm: 279.4,
    family: 'us',
    electron: 'Letter',
  },
  { id: 'A4', label: 'A4', widthMm: 210, heightMm: 297, family: 'iso', electron: 'A4' },
  {
    id: 'Legal',
    label: 'US Legal',
    widthMm: 215.9,
    heightMm: 355.6,
    family: 'us',
    electron: 'Legal',
  },
  {
    id: 'Tabloid',
    label: 'Tabloid',
    widthMm: 279.4,
    heightMm: 431.8,
    family: 'us',
    electron: 'Tabloid',
  },
  { id: 'A3', label: 'A3', widthMm: 297, heightMm: 420, family: 'iso', electron: 'A3' },
  { id: 'A2', label: 'A2 (poster)', widthMm: 420, heightMm: 594, family: 'iso', electron: 'A2' },
  { id: 'A1', label: 'A1 (poster)', widthMm: 594, heightMm: 841, family: 'iso', electron: 'A1' },
  { id: 'A0', label: 'A0 (poster)', widthMm: 841, heightMm: 1189, family: 'iso', electron: 'A0' },
];

export const DEFAULT_PAPER: PaperId = 'A4';

export function paperById(id: string): PaperSpec | null {
  return PAPER_SIZES.find((p) => p.id === id) ?? null;
}

function paperArea(spec: PaperSpec): number {
  return spec.widthMm * spec.heightMm;
}

/**
 * The next size genuinely worth trying, for the overflow finding.
 *
 * Bigger in area, and in the same family where that family has one — so A4
 * climbs the ISO stack and Letter climbs the US one until it runs out at
 * Tabloid and crosses over to the posters. Null at the top.
 */
export function nextPaperUp(id: PaperId): PaperSpec | null {
  const current = paperById(id);
  if (!current) return null;
  const bigger = PAPER_SIZES.filter((p) => paperArea(p) > paperArea(current));
  return bigger.find((p) => p.family === current.family) ?? bigger[0] ?? null;
}

/* ---------------------------------------------------------------- limits */

/** Past this it is a wiki, which is the thing the one-page design argues against. */
export const MAX_DESIGN_SECTIONS = 80;
export const MAX_DESIGN_SECTION_CHARS = 40_000;
/** The file on disk. */
export const MAX_DESIGN_DOC_CHARS = 400_000;
/** What the "copy complete document" button will hand to a model. Roughly 50k tokens. */
export const MAX_DESIGN_DOC_PROMPT_CHARS = 200_000;
/** A directory listing that stays a listing. */
export const MAX_DESIGN_DOCS_PER_PROJECT = 200;

/**
 * The legibility floor, in points, and it is ABSOLUTE — the same physical size
 * on A4 as on A0.
 *
 * Below this the sheet is not readable on paper, and at that point the talk's
 * own advice is to stop shrinking and admit the design does not fit. See
 * `onePager.ts` for what happens when it is reached.
 */
export const MIN_SHEET_BODY_PT = 7;
export const START_SHEET_BODY_PT = 10;
/** Tried in order until one fits. Halves, because a 0.1pt search is a slower way to the same answer. */
export const SHEET_BODY_PT_STEPS = [10, 9.5, 9, 8.5, 8, 7.5, 7] as const;

/**
 * The band left empty for a pencil, in millimetres.
 *
 * The marked-up sheet is the success case in the talk — he hands out pencils and
 * the scan of a level designer's scribbled-on copy is his proof it worked. The
 * layout engine treats this as out of bounds at every paper size.
 */
export const ANNOTATION_MARGIN_MM = { right: 14, bottom: 10 } as const;

/** The sheet's grid. Twelve because it divides by 2, 3, 4 and 6. */
export const SHEET_COLUMNS = 12;

/* -------------------------------------------------------------- the file */

export const DESIGN_STATUSES = ['draft', 'working', 'settled'] as const;
export type DesignStatus = (typeof DESIGN_STATUSES)[number];

export function isDesignStatus(value: unknown): value is DesignStatus {
  return typeof value === 'string' && (DESIGN_STATUSES as readonly string[]).includes(value);
}

/** One PDF that was produced. Appended on export, never edited by hand. */
export interface DesignExportRecord {
  version: number;
  /** ISO date, `YYYY-MM-DD`. */
  date: string;
  paper: PaperId;
  audience: DesignAudience;
}

/** The frontmatter. */
export interface DesignMeta {
  title: string;
  /** The local project's name, for the footer. Free text; not a server id. */
  project: string;
  status: DesignStatus;
  /** Bumped by each PDF export. Never typed. */
  version: number;
  /** ISO date, `YYYY-MM-DD`. */
  updated: string;
  audiences: DesignAudience[];
  exports: DesignExportRecord[];
  /**
   * Frontmatter lines this app does not understand, verbatim and in order.
   *
   * Preserved rather than dropped because somebody will keep a ticket id, a
   * Perforce path or their own field up there, and silently eating it on the
   * next autosave is the kind of thing that stops people trusting a tool with
   * their files.
   */
  extra: string[];
}

export interface DesignSection {
  heading: string;
  tier: DesignTier;
  /**
   * Whether the tier was written down, as opposed to defaulted.
   *
   * False for a heading with no `{tier:n}` marker. Serialising only writes a
   * marker when this is true, which is what lets a document written in another
   * editor save back byte-identically instead of sprouting a marker on every
   * heading the first time it is opened here.
   */
  tierExplicit: boolean;
  /** Everything under the heading, verbatim, without the surrounding blank lines. */
  body: string;
}

export interface DesignDoc {
  meta: DesignMeta;
  /**
   * Anything between the frontmatter and the first `##` heading.
   *
   * Usually a title line and a sentence. It is not a section — it has no tier
   * and never reaches the one-pager — but it is somebody's writing, so it
   * survives the round trip.
   */
  preamble: string;
  sections: DesignSection[];
}

/* ----------------------------------------------------------- the parser */

const FRONTMATTER_FENCE = '---';
/** `## Heading` or `## Heading {tier:2}`. Only h2 delimits a section; h3 and below stay in the body. */
const HEADING_RE = /^##\s+(.*?)\s*$/;
const TIER_MARKER_RE = /\s*\{tier:\s*([123])\s*\}\s*$/;

/**
 * Split into lines, ignoring `##` inside fenced code.
 *
 * Load-bearing rather than fussy: the starter outline's own documentation shows
 * a Markdown fence containing `## Core loop {tier:1}`, and a splitter that does
 * not track fences would cut the document in half there — inside a code block,
 * producing a section whose body is a stray closing fence.
 *
 * Tracks both ``` and ~~~ and requires the closing fence to be at least as long
 * as the opening one, which is how a fence containing a fence is written.
 */
function splitTopLevelLines(src: string): { line: string; inFence: boolean }[] {
  const out: { line: string; inFence: boolean }[] = [];
  let fence: { char: string; length: number } | null = null;

  for (const line of src.split('\n')) {
    const match = /^\s{0,3}(`{3,}|~{3,})/.exec(line);
    if (match) {
      const marker = match[1] as string;
      const char = marker[0] as string;
      if (!fence) {
        fence = { char, length: marker.length };
        out.push({ line, inFence: true });
        continue;
      }
      if (char === fence.char && marker.length >= fence.length) {
        out.push({ line, inFence: true });
        fence = null;
        continue;
      }
    }
    out.push({ line, inFence: fence !== null });
  }

  return out;
}

/** Today, as the format stores it. */
export function isoDate(at: Date = new Date()): string {
  return at.toISOString().slice(0, 10);
}

function parseFrontmatter(lines: string[]): DesignMeta {
  const meta: DesignMeta = {
    title: '',
    project: '',
    status: 'draft',
    version: 0,
    updated: isoDate(),
    audiences: [],
    exports: [],
    extra: [],
  };

  for (const line of lines) {
    if (!line.trim()) continue;
    const at = line.indexOf(':');
    if (at < 0) {
      meta.extra.push(line);
      continue;
    }

    const key = line.slice(0, at).trim();
    const raw = stripComment(line.slice(at + 1)).trim();

    switch (key) {
      case 'title':
        meta.title = unquote(raw);
        break;
      case 'project':
        meta.project = unquote(raw);
        break;
      case 'status':
        if (isDesignStatus(raw)) meta.status = raw;
        else meta.extra.push(line);
        break;
      case 'version': {
        const n = Number.parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 0) meta.version = n;
        else meta.extra.push(line);
        break;
      }
      case 'updated':
        meta.updated = unquote(raw);
        break;
      case 'audiences':
        meta.audiences = parseInlineList(raw).filter(isDesignAudience);
        break;
      case 'exports':
        meta.exports = parseExports(raw);
        break;
      default:
        meta.extra.push(line);
    }
  }

  return meta;
}

/**
 * Drop a trailing `# comment`.
 *
 * Only outside quotes: `title: "Damage # types"` is a title, not a comment, and
 * the starter frontmatter ships with real comments on `status` and `version`
 * explaining what they are — so both cases occur in normal files.
 */
function stripComment(value: string): string {
  let quote: string | null = null;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (quote) {
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === '#' && (i === 0 || value[i - 1] === ' ')) return value.slice(0, i);
  }
  return value;
}

function unquote(value: string): string {
  const first = value[0];
  if ((first === '"' || first === "'") && value.endsWith(first) && value.length >= 2) {
    return value.slice(1, -1);
  }
  return value;
}

/** `[a, b, c]` or `a, b, c`. Both are written by hand often enough to accept. */
function parseInlineList(raw: string): string[] {
  const inner = raw.startsWith('[') && raw.endsWith(']') ? raw.slice(1, -1) : raw;
  return inner
    .split(',')
    .map((part) => unquote(part.trim()))
    .filter(Boolean);
}

/** `exports: [7@2026-09-20/A4/team, 6@2026-09-14/A3/exec]` — compact, one line, still readable. */
function parseExports(raw: string): DesignExportRecord[] {
  const out: DesignExportRecord[] = [];
  for (const entry of parseInlineList(raw)) {
    const match = /^(\d+)@(\d{4}-\d{2}-\d{2})\/([^/]+)\/(.+)$/.exec(entry);
    if (!match) continue;
    const [, version, date, paper, audience] = match;
    if (!paperById(paper as string) || !isDesignAudience(audience)) continue;
    out.push({
      version: Number.parseInt(version as string, 10),
      date: date as string,
      paper: paper as PaperId,
      audience,
    });
  }
  return out;
}

function serialiseExports(records: DesignExportRecord[]): string {
  return `[${records.map((r) => `${r.version}@${r.date}/${r.paper}/${r.audience}`).join(', ')}]`;
}

/**
 * Read a document.
 *
 * Total: there is no invalid design document. A file with no frontmatter, no
 * headings or nothing at all parses into something this app can show, because
 * the alternative is an editor that refuses to open a file the user can plainly
 * see in their project folder.
 *
 * CRLF is normalised to LF on the way in. The repo is LF, Unreal projects on
 * Windows are frequently not, and a document that round-trips only when its
 * line endings happen to match ours is a promise that breaks on somebody else's
 * machine.
 */
export function parseDesignDoc(source: string): DesignDoc {
  const src = source.replace(/\r\n/g, '\n');
  const lines = src.split('\n');

  let meta: DesignMeta;
  let rest: string[];

  if (lines[0]?.trim() === FRONTMATTER_FENCE) {
    const close = lines.findIndex((line, i) => i > 0 && line.trim() === FRONTMATTER_FENCE);
    if (close > 0) {
      meta = parseFrontmatter(lines.slice(1, close));
      rest = lines.slice(close + 1);
    } else {
      meta = parseFrontmatter([]);
      rest = lines;
    }
  } else {
    meta = parseFrontmatter([]);
    rest = lines;
  }

  const body = rest.join('\n');
  const marked = splitTopLevelLines(body);

  const preambleLines: string[] = [];
  const sections: DesignSection[] = [];
  let current: { heading: string; tier: DesignTier; tierExplicit: boolean; body: string[] } | null =
    null;

  for (const { line, inFence } of marked) {
    const heading = inFence ? null : HEADING_RE.exec(line);
    if (heading) {
      if (current) sections.push(finishSection(current));
      const rawHeading = heading[1] as string;
      const tierMatch = TIER_MARKER_RE.exec(rawHeading);
      current = {
        heading: tierMatch ? rawHeading.slice(0, tierMatch.index).trim() : rawHeading.trim(),
        tier: tierMatch
          ? (Number.parseInt(tierMatch[1] as string, 10) as DesignTier)
          : DEFAULT_DESIGN_TIER,
        tierExplicit: tierMatch !== null,
        body: [],
      };
      continue;
    }

    if (current) current.body.push(line);
    else preambleLines.push(line);
  }

  if (current) sections.push(finishSection(current));

  return { meta, preamble: preambleLines.join('\n').trim(), sections };
}

function finishSection(open: {
  heading: string;
  tier: DesignTier;
  tierExplicit: boolean;
  body: string[];
}): DesignSection {
  return {
    heading: open.heading,
    tier: open.tier,
    tierExplicit: open.tierExplicit,
    body: open.body.join('\n').trim(),
  };
}

/**
 * Write a document back out.
 *
 * The inverse of `parseDesignDoc` for anything this app did not change. Blank
 * lines around headings are normalised to exactly one, which is what the parser
 * trims on the way in — so the pair is stable after one cycle even on a file
 * that arrived with three.
 */
export function serialiseDesignDoc(doc: DesignDoc): string {
  const { meta } = doc;

  const front = [
    FRONTMATTER_FENCE,
    `title: ${meta.title}`,
    `project: ${meta.project}`,
    `status: ${meta.status}`,
    `version: ${meta.version}`,
    `updated: ${meta.updated}`,
    `audiences: [${meta.audiences.join(', ')}]`,
    ...(meta.exports.length ? [`exports: ${serialiseExports(meta.exports)}`] : []),
    ...meta.extra,
    FRONTMATTER_FENCE,
  ];

  const parts: string[] = [front.join('\n')];
  if (doc.preamble.trim()) parts.push(doc.preamble.trim());

  for (const section of doc.sections) {
    const marker = section.tierExplicit ? ` {tier:${section.tier}}` : '';
    const head = `## ${section.heading}${marker}`;
    parts.push(section.body.trim() ? `${head}\n\n${section.body.trim()}` : head);
  }

  return `${parts.join('\n\n')}\n`;
}

/* -------------------------------------------------------------- creation */

/**
 * The outline a new document opens with: headings, pre-tiered, no body.
 *
 * Taken from the worked examples in the talk rather than from a 90s design-bible
 * template, which is the thing it opens by arguing against. Every heading here
 * is a question somebody will ask about the game; none of them is a box to tick,
 * and the UI says so — a document is not more finished for having all ten.
 */
export const STARTER_OUTLINE: readonly { heading: string; tier: DesignTier; hint: string }[] = [
  { heading: 'The claim', tier: 1, hint: 'One sentence. What is this and why does it exist?' },
  { heading: 'Core loop', tier: 1, hint: 'What the player does, over and over.' },
  {
    heading: 'The shape of it',
    tier: 1,
    hint: 'A flow, a map, a matrix or a set of axes — the relationship that matters most.',
  },
  {
    heading: 'Time and progression',
    tier: 1,
    hint: 'Where the player should be, and when. Put a real unit on it.',
  },
  { heading: 'Systems', tier: 2, hint: 'Each part, and what it does to the others.' },
  { heading: 'Content and scale', tier: 2, hint: 'How much of what.' },
  {
    heading: 'Feel and presentation',
    tier: 2,
    hint: 'What it looks and sounds like when it is working.',
  },
  { heading: 'Risks and unknowns', tier: 2, hint: 'What could make this not work.' },
  { heading: 'Rejected', tier: 3, hint: 'What we tried, and why it is not this.' },
  { heading: 'Reference', tier: 3, hint: 'Prior art, links, notes.' },
];

export function createDesignDoc(opts: {
  title: string;
  project: string;
  outline: 'starter' | 'empty';
  today?: string;
}): DesignDoc {
  const today = opts.today ?? isoDate();
  return {
    meta: {
      title: opts.title,
      project: opts.project,
      status: 'draft',
      version: 0,
      updated: today,
      audiences: ['team'],
      exports: [],
      extra: [],
    },
    preamble: '',
    sections:
      opts.outline === 'empty'
        ? []
        : STARTER_OUTLINE.map((row) => ({
            heading: row.heading,
            tier: row.tier,
            tierExplicit: true,
            body: '',
          })),
  };
}

/* --------------------------------------------------------------- helpers */

/**
 * A filename stem from a title.
 *
 * ASCII, lowercase, hyphenated, and never empty — this becomes a real path in
 * somebody's Unreal project, on a filesystem that may be case-insensitive and
 * will certainly object to a colon.
 */
export function designSlug(title: string): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return slug || 'untitled';
}

export function isSectionEmpty(section: DesignSection): boolean {
  return section.body.trim().length === 0;
}

/**
 * Essential sections with nothing written in them.
 *
 * Shown above the Generate button rather than blocking it. Compressing a
 * half-written document produces a confident page about nothing, and the person
 * who can tell whether that matters is the one who left the section empty.
 */
export function emptyEssentialSections(doc: DesignDoc): string[] {
  return doc.sections.filter((s) => s.tier === 1 && isSectionEmpty(s)).map((s) => s.heading);
}

/** What the whole document costs, for the prompt clamp and the editor's own limits. */
export function designDocCharCount(doc: DesignDoc): number {
  return serialiseDesignDoc(doc).length;
}

/** Sections that can reach a sheet, in document order. Tier 3 never does. */
export function compressibleSections(doc: DesignDoc): DesignSection[] {
  return doc.sections.filter((s) => s.tier !== 3 && !isSectionEmpty(s));
}

/* ----------------------------------------------------------- the summary */

/** One document as the list screen shows it, without reading the whole file. */
export interface DesignDocSummary {
  slug: string;
  title: string;
  status: DesignStatus;
  version: number;
  updated: string;
  sectionCount: number;
  emptyEssentialCount: number;
  /** Whether a generated sheet is sitting next to it. */
  hasSheet: boolean;
}

export const designDocSummarySchema = z.object({
  slug: z.string(),
  title: z.string(),
  status: z.enum(DESIGN_STATUSES),
  version: z.number().int().min(0),
  updated: z.string(),
  sectionCount: z.number().int().min(0),
  emptyEssentialCount: z.number().int().min(0),
  hasSheet: z.boolean(),
});
